import { Button } from '@material-ui/core';
import React from 'react';
import { Control, useForm, DeepMap, FieldError } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import {
  AppDatePicker,
  AppSelect,
  AppTextField,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { Translations } from '../../configuration';
import { useHistory } from 'react-router';
import NavigationConfig from 'navigation/config';
import * as VatRateSelectors from 'redux/reducers/vat_rate_reducer/selectors';
import { StateStatus } from 'redux/utils/common';

interface FormFields {
  country_code: string | null;
  vat_rate_type_id: string | null;
  valid_from: string | null;
  valid_to: string | null;
}

interface IProps {
  country_code: string | null;
  vat_rate_type_id: string | null;
  valid_from: string | null;
  valid_to: string | null;
}

const FilterForm = (props: IProps) => {
  const history = useHistory();
  const defaultValues = {
    country_code: props.country_code ? props.country_code : null,
    vat_rate_type_id: props.vat_rate_type_id ? props.vat_rate_type_id : null,
    valid_from: props.valid_from ? props.valid_from : null,
    valid_to: props.valid_to ? props.valid_to : null,
  };
  const { control, errors, handleSubmit } = useForm<FormFields>({
    defaultValues: defaultValues,
  });

  const onSubmit = (data: any) => {
    let query: { key: string; value: any }[] = [];
    if (data.country_code) query.push({ key: 'country_code', value: data.country_code });
    if (data.vat_rate_type_id) query.push({ key: 'vat_rate_type_id', value: data.vat_rate_type_id });
    if (data.valid_from) query.push({ key: 'valid_from', value: new Date(data.valid_from).toISOString().split('T')[0] });
    if (data.valid_to) query.push({ key: 'valid_to', value: new Date(data.valid_to).toISOString().split('T')[0] });
    let queryString = '';
    query.map((q, index) => {
      if (index === 0) queryString += `?${q.key}=${q.value}`;
      else queryString += `&${q.key}=${q.value}`;
    });
    history.push(NavigationConfig.vatRatesPage().path + queryString);
  };

  const clearFilter = () => {
    history.push(NavigationConfig.vatRatesPage().path);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <GridLayout
        elements={[
          {
            element: renderCountryCodeField(errors, control),
            size: 6,
            id: 'country_code',
          },
          {
            element: renderVatRateTypeField(errors, control),
            size: 6,
            id: 'vat_rate_type_id',
          },
          {
            element: renderValidFromField(errors, control),
            size: 6,
            id: 'valid_from',
          },
          {
            element: renderValidToField(errors, control),
            size: 6,
            id: 'valid_to',
          },
          {
            element: <span></span>,
            size: 8,
            id: 'space',
          },
          {
            element: (
              <Button
                variant="contained"
                color="primary"
                style={{ width: '100%', marginBottom: '1rem' }}
                onClick={clearFilter}
              >
                <TranslatedText textMap={Translations.clear} />
              </Button>
            ),
            size: 2,
            id: 'clear',
          },
          {
            element: (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ width: '100%', marginBottom: '1rem' }}
              >
                <TranslatedText textMap={Translations.search} />
              </Button>
            ),
            size: 2,
            id: 'search',
          },
        ]}
      />
    </form>
  );
};

const renderCountryCodeField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      id="country_code"
      label={<TranslatedText textMap={Translations.country_code_filter} />}
      error={'country_code' in errors}
      name="country_code"
      control={control}
      rules={{}}
      helperText={errors.country_code && errors.country_code.message}
    />
  );
};

const renderVatRateTypeField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  const vatRateTypes = VatRateSelectors.useGetVatRateTypes();
  const options: any[] = [{ id: null, label: Translations.vat_rate_type_filter.English }];
  if (vatRateTypes && vatRateTypes.status === StateStatus.Success && vatRateTypes.data) {
    vatRateTypes.data.map((t: any) => {
      options.push({ id: `${t.vat_rate_type_id}`, label: t.description });
    });
  }
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.vat_rate_type_filter} />}
      id="vat_rate_type_id"
      name="vat_rate_type_id"
      error={'vat_rate_type_id' in errors}
      control={control}
      rules={{}}
      options={options}
      helperText={errors.vat_rate_type_id && errors.vat_rate_type_id.message}
    />
  );
};

const renderValidFromField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      autoOk
      margin="normal"
      label={<TranslatedText textMap={Translations.valid_from_filter} />}
      format={'yyyy-MM-dd'}
      error={'valid_from' in errors}
      control={control}
      rules={{}}
      name="valid_from"
      helperText={errors.valid_from && errors.valid_from.message}
    />
  );
};

const renderValidToField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      autoOk
      margin="normal"
      label={<TranslatedText textMap={Translations.valid_to_filter} />}
      format={'yyyy-MM-dd'}
      error={'valid_to' in errors}
      control={control}
      rules={{}}
      name="valid_to"
      helperText={errors.valid_to && errors.valid_to.message}
    />
  );
};

export default FilterForm;
